import { Inject, Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";

import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import {
    GridCategory,
    GridFacade,
    GridMenuLevel,
} from "@hermes/states/grid-filters-state";
import {
    getGridPath,
    MenuFacade,
    CategoryEntry,
    FlatCategories,
} from "@hermes/states/menu";
import { LOGGER } from "@hermes/web-logger";

/**
 * Category guard : redirect to 404 if the route is not found in the category tree.
 */
@Injectable()
export class CategoryGuard implements CanActivate {
    private noCategoryFound = "Grid path not found";
    constructor(
        @Inject(LOCALE) private locale: Locale,
        @Inject(LOGGER) private logger: Logger,
        private router: Router,
        private menuFacade: MenuFacade,
        private gridFacade: GridFacade,
    ) {}

    /**
     * @returns true if a category corresponding to the url in the route exists
     */
    public canActivate(
        _route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> {
        const fullPath = this.locale.urlPrefix + state.url;
        const fullPathWithoutQueryParameters = fullPath.split("?")[0];
        const cleanPath = fullPathWithoutQueryParameters.split("#")[0];

        this.menuFacade.fetchMenu();

        return this.menuFacade.categories$.pipe(
            filter((categories) => !!categories),
            switchMap((categories) => {
                const category = findCategoryMatchingPath(
                    cleanPath,
                    categories,
                );

                if (!category) {
                    throw new Error(this.noCategoryFound);
                }

                const parentCategory = findParentCategory(category, categories);
                const masterCategory = findParentCategory(
                    parentCategory,
                    categories,
                );

                let gridCategory: GridCategory;
                if (
                    category.name.length > 0 &&
                    masterCategory.name.length > 0
                ) {
                    gridCategory = {
                        name: category.name,
                        pimCode: category.pimCode ?? "",
                        masterCategoryName: masterCategory.name,
                    };
                }

                this.gridFacade.initGridCategory(
                    gridCategory,
                    getMenuLevel(category, parentCategory, masterCategory),
                );

                return this.gridFacade.category$.pipe(
                    filter((gridCategory) => !!gridCategory?.pimCode),
                    map(() => true),
                    take(1),
                );
            }),
            // otherwise, something went wrong
            catchError((error) => {
                if (!error.message || error.message !== this.noCategoryFound) {
                    this.logger.error(
                        "Unexpected error while processing category routing",
                        error,
                    );
                }
                // redirect to 404-page without changing the url
                this.router.navigate(["/404-error"], {
                    // minimal effect. see https://github.com/angular/angular/issues/17004
                    skipLocationChange: true,
                    // path the originalUrl
                    queryParams: {
                        originalUrl: state.url,
                    },
                });
                return of(false);
            }),
        );
    }
}

function findCategoryMatchingPath(
    path: string,
    categories: FlatCategories,
): CategoryEntry | undefined {
    return Object.values(categories).find(
        (category) => getGridPath(category) === path,
    );
}

function findParentCategory(
    categoryEntry: CategoryEntry,
    categories: FlatCategories,
): CategoryEntry {
    return (
        Object.values(categories).find(
            (category) => category.tid === categoryEntry.parentId,
        ) || categoryEntry
    );
}

function getMenuLevel(
    category: CategoryEntry,
    parentCategory: CategoryEntry,
    masterCategory: CategoryEntry,
): GridMenuLevel {
    if (category.pimCode === masterCategory.pimCode) {
        return {
            menuLv1: masterCategory.pimCode ?? "",
        };
    }

    if (parentCategory.pimCode === masterCategory.pimCode) {
        return {
            menuLv1: masterCategory.pimCode ?? "",
            menuLv2: category.pimCode,
        };
    }

    return {
        menuLv1: masterCategory.pimCode ?? "",
        menuLv2: parentCategory.pimCode,
        menuLv3: category.pimCode,
    };
}
